/**
 * Data Visualization Module - Challenge Configuration
 *
 * 10 progressive challenges covering 4 chapters:
 * Chapter 1 - Building Blocks  : single-table, one dimension  (Q1-Q3)
 * Chapter 2 - Trends & Ranking : time series + Top-N LIMIT     (Q4-Q5)
 * Chapter 3 - Joins            : filters, joins, relative dates (Q6-Q8)
 * Chapter 4 - Advanced Metrics : avg per user, % share          (Q9-Q10)
 *
 * Available columns
 *   users  : user_id, signup_date, country, city, device, age_group
 *   events : event_id, user_id, event_name, event_date
 *
 *   event_name values : login, view_product, purchase
 */

export const DATA_VIZ_LEVELS = [
    {
        id: 1,
        title: 'Building Blocks',
        description: 'Single-table charts with one dimension',
        color: '#10b981',
    },
    {
        id: 2,
        title: 'Trends & Ranking',
        description: 'Time series over dates and Top-N ranked results',
        color: '#3b82f6',
    },
    {
        id: 3,
        title: 'Joins',
        description: 'Connect two tables to answer cross-table questions',
        color: '#8b5cf6',
    },
    {
        id: 4,
        title: 'Advanced Metrics',
        description: 'Normalised metrics and percentage share views',
        color: '#f97316',
    },
];

export const DATA_VIZ_CHALLENGES = [

    // ═══ Chapter 1 - Building Blocks ════════════════════════════════
    {
        id: 'user-growth-slowing',
        level: 1,
        title: 'Q1. User Growth Slowing Down?',
        businessContext: `The exec team thinks growth is slowing - but is that actually true, or just a feeling? **Before we investigate why, we need to establish the facts.** Let's chart the number of new user signups over time and see if the curve is actually flattening.`,
        question: 'Create a line chart showing the number of user signups over time.',
        goal: 'Line chart showing the number of user signups over time.',
        hint: `Group by \`signup_date\` at **Week** granularity so individual days don't create a noisy chart.
1. Select the \`users\` table.
2. Add a Group By for \`signup_date\` and set granularity to **Week**.
3. Set the Metric to **COUNT** of \`user_id\`.
4. Run the query and select **Line** chart.`,
        teach: 'Looking at the overall growth trajectory first gives us a baseline before we zoom into segments.',
        explanation: 'We can see the overall signup curve is indeed flattening out recently. Let\'s dig deeper to see if this is a global trend or isolated to specific countries.',
        expectedChartType: 'line',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'signup_date', granularity: 'week' }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'slowdown-by-region',
        level: 1,
        title: 'Q2. Is the slowdown global or regional?',
        businessContext: `The trend is real - but a global slowdown and a regional one require completely different responses. **If it's one country pulling down the aggregate, that's where we focus.** Let's filter to this year and break signups down by country to find the culprit.`,
        question: 'Create a line chart showing signups this year, broken down by country.',
        goal: 'Line chart showing signups this year, broken down by country.',
        hint: `Use a relative date filter to keep data current, then add a second Group By dimension for \`country\` - Recharts will automatically plot one line per country.
1. Select the \`users\` table.
2. Add a **Filter**: \`signup_date\` → **Relative** → **This Year**.
3. Add a Group By for \`signup_date\` (granularity: **Week**) and a second Group By for \`country\`.
4. Set the Metric to **COUNT** of \`user_id\`.
5. Run and select **Line** chart.`,
        teach: 'Breaking down recent data by country quickly reveals whether the slowdown is everywhere or isolated - a critical distinction for deciding where to invest investigation time.',
        explanation: 'By plotting weekly signups per country this year, we can see that while other regions are steady, the UK is clearly dragging down the aggregate numbers.',
        expectedChartType: 'line',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'signup_date', granularity: 'week' }, { column: 'country' }],
            filters: [{ column: 'signup_date', operator: 'RELATIVE', value: 'this_year' }],
            join: null,
        },
    },

    {
        id: 'uk-signup-share',
        level: 1,
        title: 'Q3. How big is the UK\'s share of our signups?',
        businessContext: `The UK looks like it's declining - but **how much does that actually matter for the business?** A country with 1% of signups dropping off is noise; a country with 30% dropping off is a crisis. Let's look at the composition of signups this year to understand the UK's weight.`,
        question: 'Create a pie chart showing the distribution of signups by country this year.',
        goal: 'Pie chart showing the distribution of signups by country this year.',
        hint: `Keep the same filter as Q2, but drop the date Group By - we want total share, not a time series. Switch to a Pie chart type.
1. Select the \`users\` table.
2. Add a **Filter**: \`signup_date\` → **Relative** → **This Year**.
3. Add a Group By for \`country\` only (remove any date group-by).
4. Set the Metric to **COUNT** of \`user_id\`.
5. Run and select **Pie** chart.`,
        teach: 'A pie chart shows proportional composition at a glance. It\'s the right tool when you want to answer "what fraction of the total is X?" rather than tracking change over time.',
        explanation: 'This chart reveals that the UK is a significant portion of our market, meaning its slowdown has a major impact on overall business growth.',
        expectedChartType: 'pie',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'country' }],
            filters: [{ column: 'signup_date', operator: 'RELATIVE', value: 'this_year' }],
            join: null,
        },
    },

    // ═══ Chapter 2 - Trends & Ranking ════════════════════════════════

    {
        id: 'uk-engagement-decline',
        level: 2,
        title: 'Q4. Is UK engagement also declining?',
        businessContext: `The UK is a big market and signups are slowing - but is this a *new user* problem or does it also affect *existing users*? **If engagement is fine, maybe our acquisition campaigns just stopped working. If engagement is also down, it's a product problem.** Let's check login activity across countries as a proxy for active engagement.`,
        question: 'Create a bar chart showing total login events by country.',
        goal: 'Bar chart showing total login events by country.',
        hint: `Login events live in \`events\`, but country is on \`users\` - you'll need a JOIN. Filter for \`login\` events only so other event types don't pollute the count.
1. Select the \`events\` table.
2. Add a **Join** with the \`users\` table on \`user_id\`.
3. Add a **Filter**: \`event_name\` = **login**.
4. Add a Group By for \`country\`.
5. Set the Metric to **COUNT** of \`event_id\`.
6. Run and select **Bar** chart.`,
        teach: 'Logins are the simplest proxy for active users. By joining `events` to `users`, we can attach the country dimension to behavioural data that doesn\'t natively carry it.',
        explanation: 'Login activity is indeed lowest in the UK, confirming that both new acquisition and existing user engagement are suffering there.',
        expectedChartType: 'bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: false, limit: false,
        },
        answer: {
            table: 'events',
            aggregation: { fn: 'COUNT', column: 'event_id' },
            groupBy: [{ column: 'country' }],
            filters: [{ column: 'event_name', operator: '=', value: 'login' }],
            join: { table: 'users', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    },

    {
        id: 'uk-login-drop-by-device',
        level: 2,
        title: 'Q5. Which device is driving the UK login drop?',
        businessContext: `Both acquisition and engagement are down in the UK. The next question is: **is this affecting all devices equally, or is one platform underperforming?** A platform-specific problem (e.g. broken Android app) would look very different to a general market issue.`,
        question: 'Create a stacked bar chart showing monthly login events in the UK, broken down by device.',
        goal: '100% stacked bar chart showing monthly login events in the UK, broken down by device.',
        hint: `You need two filters (UK + login) and two Group By dimensions (date + device). Use Stacked Bar, not plain Bar, to see each device's share of the month's total.
1. Select the \`events\` table.
2. Add a **Join** with \`users\` on \`user_id\`.
3. Add two Filters: \`country\` = **UK** and \`event_name\` = **login**.
4. Add a Group By for \`event_date\` (granularity: **Month**) and a second for \`device\`.
5. Set the Metric to **COUNT** of \`event_id\`.
6. Run and select **Stacked Bar** chart.`,
        teach: '100% stacked bar charts are ideal when you want to see how the *composition* of a total shifts over time - perfect for spotting if one segment (like Android) is shrinking relative to others.',
        explanation: 'The chart shows that Android\'s share of login activity in the UK is steadily decreasing over time relative to iOS and Web.',
        expectedChartType: 'stacked-bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'events',
            aggregation: { fn: 'COUNT', column: 'event_id' },
            groupBy: [{ column: 'event_date', granularity: 'month' }, { column: 'device' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }, { column: 'event_name', operator: '=', value: 'login' }],
            join: { table: 'users', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    },

    // ═══ Chapter 3 - Joins ══════════════════════════════════════════

    {
        id: 'uk-android-signup-decline',
        level: 3,
        title: 'Q6. Are Android signups also declining in the UK?',
        businessContext: `We've established that Android's share of UK logins is falling. But **is this because existing Android users are disengaging, or because fewer new Android users are signing up?** If acquisition is the problem, it's a marketing issue. If it's engagement, it's a product issue - or something broke in the app.`,
        question: 'Create a line chart showing UK signups over time, broken down by device.',
        goal: 'Line chart showing UK signups over time, broken down by device.',
        hint: `Switch back to the \`users\` table for signup data. Filter to UK only, then break by device to see each platform's acquisition trend.
1. Select the \`users\` table.
2. Add a **Filter**: \`country\` = **UK**.
3. Add a Group By for \`signup_date\` (granularity: **Week**) and a second for \`device\`.
4. Set the Metric to **COUNT** of \`user_id\`.
5. Run and select **Line** chart.`,
        teach: 'Switching from events back to users and grouping by `device` lets us check if the acquisition pipeline itself is platform-biased - without needing a join.',
        explanation: 'Android signups in the UK are also decreasing. This suggests our issue is a platform-specific problem affecting both acquisition and engagement on Android in the UK.',
        expectedChartType: 'line',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'signup_date', granularity: 'week' }, { column: 'device' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }],
            join: null,
        },
    },

    {
        id: 'uk-android-user-engagement',
        level: 3,
        title: 'Q7. Are newer UK Android users just less engaged?',
        businessContext: `Both Android acquisition and engagement are down in the UK. But there are two possible explanations: **are our older Android users churning (engagement fading over time), or are the new users we're acquiring just lower quality?** A cohort analysis answers this directly by grouping users by when they signed up.`,
        question: 'Create a bar chart showing login activity grouped by event week and signup month for UK users over the last 2 months.',
        goal: 'Bar chart showing login activity grouped by event week and signup month for UK users over the last 2 months.',
        hint: `This is the most complex query yet - it joins tables, filters by multiple conditions, uses relative dates, and groups by two different time dimensions.
1. Select the \`users\` table.
2. Add a **Join** with \`events\` on \`user_id\`.
3. Add three Filters: \`country\` = **UK**, \`event_name\` = **login**, \`event_date\` → **Relative** → **Last 2 Months**.
4. Add a Group By for \`event_date\` (granularity: **Week**) and a second for \`signup_date\` (granularity: **Month**).
5. Set the Metric to **Count Distinct** of \`user_id\`.
6. Run and select **Bar** chart.`,
        teach: 'A cohort analysis groups users by their signup period and then measures their behaviour. If newer cohorts are less active, the product for new users may have gotten worse - or the users acquired recently are less valuable.',
        explanation: 'The chart indicates that recent cohorts of Android users in the UK are significantly less active than older cohorts, pointing to a recent app update or marketing change.',
        expectedChartType: 'bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT_DISTINCT', column: 'user_id' },
            groupBy: [{ column: 'event_date', granularity: 'week' }, { column: 'signup_date', granularity: 'month' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }, { column: 'event_name', operator: '=', value: 'login' }, { column: 'event_date', operator: 'RELATIVE', value: 'LAST_2_MONTHS' }],
            join: { table: 'events', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    },

    {
        id: 'uk-android-dropoff-funnel',
        level: 3,
        title: 'Q8. Where exactly are UK Android users dropping off?',
        businessContext: `We know newer Android users in the UK are less engaged. The next question is: **at what step in the product journey are they abandoning?** Our funnel has three events: \`login\` → \`view_product\` → \`purchase\`. If Android users drop steeply at purchase but not at view, that points directly to a checkout bug.`,
        question: 'Create a bar chart showing how many UK users reach each funnel step, broken down by device.',
        goal: 'Bar chart showing how many UK users reach each funnel step, broken down by device.',
        hint: `Use Count Distinct of \`user_id\` (not Count of \`event_id\`) - you want unique users per step, not raw event volume.
1. Select the \`events\` table.
2. Add a **Join** with \`users\` on \`user_id\`.
3. Add a **Filter**: \`country\` = **UK**.
4. Add a Group By for \`event_name\` and a second for \`device\`.
5. Set the Metric to **Count Distinct** of \`user_id\`.
6. Run and select **Bar** chart.`,
        teach: 'Funnel analysis counts distinct users at each stage. Using Count Distinct is critical here - an event count would inflate for users who trigger the same event multiple times.',
        explanation: 'Android users in the UK drop off heavily right before purchasing, highlighting a major friction point or bug in their checkout journey.',
        expectedChartType: 'bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: false, limit: false,
        },
        answer: {
            table: 'events',
            aggregation: { fn: 'COUNT_DISTINCT', column: 'user_id' },
            groupBy: [{ column: 'event_name' }, { column: 'device' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }],
            join: { table: 'users', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    },

    // ═══ Chapter 4 - Advanced Metrics ════════════════════════════════

    {
        id: 'android-purchase-drop-trend',
        level: 4,
        title: 'Q9. Is the Android purchase drop getting worse?',
        businessContext: `We've identified a funnel drop-off on Android at the purchase step. Now **we need to quantify the revenue impact over time.** Is this a new problem (sudden drop) or a long-term trend (gradual decline)? Knowing the shape of the decline tells us how urgent the fix is.`,
        question: 'Create a line chart showing purchase events over time in the UK, broken down by device.',
        goal: 'Line chart showing purchase events over time in the UK, broken down by device.',
        hint: `Very similar to Q5, but now filter for \`purchase\` events instead of \`login\`, and use a Line chart to show the trend over time.
1. Select the \`events\` table.
2. Add a **Join** with \`users\` on \`user_id\`.
3. Add two Filters: \`country\` = **UK**, \`event_name\` = **purchase**.
4. Add a Group By for \`event_date\` (granularity: **Week**) and a second for \`device\`.
5. Set the Metric to **COUNT** of \`event_id\`.
6. Run and select **Line** chart.`,
        teach: 'Plotting purchases over time by device makes the revenue impact of the Android issue immediately visible - the slope of the Android line tells us if this is accelerating or stable.',
        explanation: 'We can see a clear decline in purchases driven solely by Android devices in the UK, validating the massive funnel drop-off.',
        expectedChartType: 'line',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: true, limit: false,
        },
        answer: {
            table: 'events',
            aggregation: { fn: 'COUNT', column: 'event_id' },
            groupBy: [{ column: 'event_date', granularity: 'week' }, { column: 'device' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }, { column: 'event_name', operator: '=', value: 'purchase' }],
            join: { table: 'users', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    },

    {
        id: 'android-conversion-rate',
        level: 4,
        title: 'Q10. Proof: Android\'s conversion rate is worst',
        businessContext: `Purchases are down on Android in the UK - but someone might argue: *"that's just because fewer Android users signed up."* We need a metric that's **immune to volume differences**: purchase conversion rate. If Android's rate is genuinely lower than iOS and Web, the product experience itself is broken - not just the acquisition funnel.`,
        question: 'Create a bar chart showing the purchase conversion rate by device for UK users.',
        goal: 'Bar chart showing purchase conversion rate by device.',
        hint: `This requires a **Custom Expression** that divides distinct purchasers by distinct users per device.
1. Select the \`users\` table.
2. Add a **Join** with \`events\` on \`user_id\`.
3. Add a **Filter**: \`country\` = **UK**.
4. Add a Group By for \`device\`.
5. Set the Metric to **Custom Expression**: \`COUNT(DISTINCT CASE WHEN event_name = 'purchase' THEN users.user_id END) / COUNT(DISTINCT users.user_id)\`
6. Run and select **Bar** chart.`,
        teach: 'Conversion rate = purchasers ÷ total users. Using `CASE WHEN` inside `COUNT(DISTINCT ...)` is a powerful SQL pattern for "count only the subset that meets a condition" without needing a subquery.',
        explanation: 'Android has the lowest conversion rate. The slowdown in the UK is definitely driven by a broken checkout experience specifically for Android users in that region!',
        expectedChartType: 'bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: true, join: true, granularity: false, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'CUSTOM', expression: 'COUNT(DISTINCT CASE WHEN event_name = \'purchase\' THEN users.user_id END) / COUNT(DISTINCT users.user_id)', alias: 'conversion_rate' },
            groupBy: [{ column: 'device' }],
            filters: [{ column: 'country', operator: '=', value: 'UK' }],
            join: { table: 'events', type: 'JOIN', leftCol: 'user_id', rightCol: 'user_id' },
        },
    }
];

/**
 * Validate user's query state against the expected answer
 * Returns { passed: boolean, feedback: string }
 */
export function validateAnswer(userState, challenge) {
    const expected = challenge.answer;
    const issues = [];

    // Check table
    if (userState.table !== expected.table) {
        issues.push(`Wrong table. Expected "${expected.table}".`);
    }

    // Check join
    if (expected.join) {
        if (!userState.join || !userState.join.table) {
            issues.push('You need to add a join.');
        } else if (userState.join.table !== expected.join.table) {
            issues.push(`Wrong join table. Expected "${expected.join.table}".`);
        }
    } else if (userState.join && userState.join.table) {
        issues.push('This question does not require a join.');
    }

    // Check aggregation - match against any metric in the metrics array (or legacy single aggregation)
    if (expected.aggregation) {
        const metricsToCheck = (userState.metrics && userState.metrics.length > 0)
            ? userState.metrics
            : (userState.aggregation ? [userState.aggregation] : []);
        const match = metricsToCheck.find(m => m.fn === expected.aggregation.fn);
        if (!match) {
            issues.push(`Use ${expected.aggregation.fn} as the aggregation function.`);
        }
    }

    // Check group by columns (order-independent)
    if (expected.groupBy && expected.groupBy.length > 0) {
        const expectedCols = expected.groupBy.map(g => g.column).sort();
        const userCols = (userState.groupBy || []).map(g => g.column).sort();
        if (JSON.stringify(expectedCols) !== JSON.stringify(userCols)) {
            issues.push(`Group by: expected [${expectedCols.join(', ')}], got [${userCols.join(', ')}].`);
        }
    }

    // Check filters - include IS NOT NULL filters (they have no value)
    if (expected.filters && expected.filters.length > 0) {
        const userFilters = (userState.filters || []).filter(f =>
            f.column && (f.operator === 'IS NOT NULL' || (f.value !== '' && f.value !== undefined))
        );
        if (userFilters.length === 0) {
            issues.push('You need to add a filter.');
        } else {
            expected.filters.forEach(ef => {
                const match = userFilters.find(uf =>
                    uf.column === ef.column &&
                    uf.operator === ef.operator &&
                    (ef.operator === 'IS NOT NULL' ||
                        ef.operator === 'IN'
                        ? String(uf.value).split(',').map(v => v.trim()).sort().join(',') ===
                        String(ef.value).split(',').map(v => v.trim()).sort().join(',')
                        : String(uf.value).toLowerCase() === String(ef.value).toLowerCase())
                );
                if (!match) {
                    const valDesc = ef.operator === 'IS NOT NULL' ? '' : ` ${ef.value}`;
                    issues.push(`Missing filter: ${ef.column} ${ef.operator}${valDesc}`);
                }
            });
        }
    }

    // Check limit
    if (expected.limit) {
        if (!userState.limit || Number(userState.limit) !== Number(expected.limit)) {
            issues.push(`Set the Limit to ${expected.limit}.`);
        }
    }

    // Check chart type
    if (challenge.expectedChartType) {
        if (!userState.chartType || userState.chartType !== challenge.expectedChartType) {
            issues.push(`Use a ${challenge.expectedChartType} chart.`);
        }
    }

    if (issues.length === 0) {
        return { passed: true, feedback: '\u2705 Perfect! Your query matches the expected answer.' };
    }
    return { passed: false, feedback: issues.join('\n') };
}
